import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';

/**
 * 晋升提名汇总（管理员）。
 *
 * V1.3 起领导端只填三项（提名人 / 晋升职级 / 晋升理由），不再选 suggestion，
 * 故本页删去「建议」列；deptPriority 改作"#"列，仅作 HR 排序参考。
 *
 * V1.4：晋升理由整段展示（不截断），并支持按当前批次一键导出 CSV。
 *
 * V1.5（2026-05）：同一被提名人被多位领导提名时合并为一行展示，
 *   提名领导 / 晋升职级 / 晋升理由 三列以"逐条对齐"方式呈现各位领导的提报；
 *   删除"提交时间"列（HR 反馈不关心，挤占晋升理由展示宽度）。
 * V1.6（2026-05）：管理员在汇总页底部可「人力资源部补录提名」，对应 POST /admin/nominations/manual，
 *   不占各提名领导配额；补录记录提名人显示为操作者本人，理由自动带【人力资源部补录】前缀（若未手写）。
 * V1.7（2026-05）：新增「序号」列展示全局连续编号（1…N，唯一不重复）；
 *   原「#」列重命名为「部门顺序」，明确语义为各部门长内部排序参考，跨部门可能出现重复属正常。
 * V1.8（2026-05）：按业务反馈删除「部门顺序」列（在合议会场景里不被使用，徒增噪声）；
 *   仅保留「序号」全局连续编号；CSV 导出表头同步去掉「部门顺序」。
 */

type NomineeCandidate = {
  id: number;
  name: string | null;
  deptPath: string | null;
};

type Nomination = {
  id: number;
  deptPriority: number | null;
  nominee: { id: number; name: string | null; deptPath: string | null };
  nominator: { id: number | null; name: string | null };
  targetRank: string | null;
  highlight: string | null;
  submitAt: string | null;
};

type MergedRow = {
  key: number;
  /** 合并行在全局排序中的连续序号（1…n），便于阅读；与部门内排序无关 */
  displayIndex: number;
  nominee: Nomination['nominee'];
  minDeptPriority: number | null;
  nominations: Nomination[];
};

function csvField(v: unknown): string {
  const s = v == null ? '' : String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function NominationsPage() {
  const { batches, batchId, setBatchId } = useBatches();
  const qc = useQueryClient();
  const [manualOpen, setManualOpen] = useState(false);
  const [form] = Form.useForm<{
    nomineeId: number;
    targetRank?: string;
    highlight: string;
  }>();

  const { data = [] } = useQuery<Nomination[]>({
    queryKey: ['nominations-admin', batchId],
    queryFn: () => api.get('/admin/nominations', { params: { batchId } }),
    enabled: !!batchId,
  });

  const { data: candidates = [] } = useQuery<NomineeCandidate[]>({
    queryKey: ['nominator-candidates'],
    queryFn: () =>
      api.get('/nominator/candidates') as Promise<NomineeCandidate[]>,
    enabled: manualOpen && !!batchId,
  });

  const manualMut = useMutation({
    mutationFn: (values: {
      nomineeId: number;
      targetRank?: string;
      highlight: string;
    }) =>
      api.post('/admin/nominations/manual', {
        batchId,
        nomineeId: values.nomineeId,
        highlight: values.highlight,
        targetRank: values.targetRank || undefined,
      }),
    onSuccess: () => {
      message.success('补录成功');
      void qc.invalidateQueries({ queryKey: ['nominations-admin', batchId] });
      setManualOpen(false);
      form.resetFields();
    },
    onError: (e: unknown) => {
      const msg =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        e.response &&
        typeof e.response === 'object' &&
        'data' in e.response &&
        e.response.data &&
        typeof e.response.data === 'object' &&
        'message' in e.response.data
          ? String((e.response.data as { message: unknown }).message)
          : '补录失败';
      message.error(msg);
    },
  });

  // 按被提名人合并：王盛被两位领导提名 → 同一行展示两位领导的提报
  const mergedData = useMemo<MergedRow[]>(() => {
    const map = new Map<number, MergedRow>();
    data.forEach((n) => {
      const id = n.nominee?.id;
      if (id == null) return;
      const cur = map.get(id);
      if (!cur) {
        map.set(id, {
          key: id,
          displayIndex: 0,
          nominee: n.nominee,
          minDeptPriority: n.deptPriority,
          nominations: [n],
        });
      } else {
        cur.nominations.push(n);
        if (
          n.deptPriority != null &&
          (cur.minDeptPriority == null || n.deptPriority < cur.minDeptPriority)
        ) {
          cur.minDeptPriority = n.deptPriority;
        }
      }
    });
    // 排序：先按最小 deptPriority 升序（同部门 HR 排序参考），再按 nominee.id 兜底
    const rows = Array.from(map.values());
    rows.forEach((r) =>
      r.nominations.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
    );
    const sorted = rows.sort((a, b) => {
      const ap = a.minDeptPriority ?? Number.MAX_SAFE_INTEGER;
      const bp = b.minDeptPriority ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      return a.key - b.key;
    });
    return sorted.map((r, i) => ({ ...r, displayIndex: i + 1 }));
  }, [data]);

  const handleExport = () => {
    if (!data.length) {
      message.warning('当前批次暂无提名数据');
      return;
    }
    // CSV 仍按"一行一条原始提名"导出，方便 HR 在 Excel 里透视。提交时间已移除。
    // V1.8：与页面对齐，仅保留「序号」全局连续编号，删除「部门顺序」列。
    const header = [
      '序号',
      '被提名人',
      '部门',
      '提名领导',
      '晋升职级',
      '晋升理由',
    ];
    const lines = [header.map(csvField).join(',')];
    data.forEach((n, i) => {
      const row = [
        i + 1,
        n.nominee?.name ?? '',
        n.nominee?.deptPath ?? '',
        n.nominator?.name ?? '',
        n.targetRank ?? '',
        n.highlight ?? '',
      ];
      lines.push(row.map(csvField).join(','));
    });
    const csv = '\ufeff' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const currentBatch = batches.find((b) => b.id === batchId);
    const batchTag = currentBatch?.name ?? 'all';
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.download = `提名汇总-${batchTag}-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success(`已导出 ${data.length} 条提名`);
  };

  // 三列共用的"按提名条目逐条对齐"渲染骨架，保证 提名领导 / 晋升职级 / 晋升理由 行高一致
  const stack = (children: React.ReactNode[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </div>
  );

  return (
    <div>
      <div className="tg-page-title">晋升提名汇总</div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            value={batchId}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!data.length}
          >
            导出 CSV
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!batchId}
            onClick={() => setManualOpen(true)}
          >
            人力资源部补录提名
          </Button>
          <span style={{ color: '#999' }}>
            共 {mergedData.length} 人 · {data.length} 条提名
          </span>
        </Space>
        <Table<MergedRow>
          rowKey="key"
          dataSource={mergedData}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          columns={[
            {
              title: '序号',
              dataIndex: 'displayIndex',
              width: 60,
              fixed: 'left',
              render: (v: number) => v,
            },
            {
              title: '被提名人',
              width: 130,
              render: (_: unknown, r: MergedRow) => (
                <Space direction="vertical" size={2}>
                  <strong>{r.nominee?.name ?? '-'}</strong>
                  {r.nominations.length > 1 && (
                    <Tag color="orange">{r.nominations.length} 人提名</Tag>
                  )}
                </Space>
              ),
            },
            {
              title: '部门',
              width: 220,
              ellipsis: true,
              render: (_: unknown, r: MergedRow) =>
                r.nominee?.deptPath ?? '-',
            },
            {
              title: '提名领导',
              width: 110,
              render: (_: unknown, r: MergedRow) =>
                stack(
                  r.nominations.map((n) => (
                    <span key={n.id}>{n.nominator?.name ?? '-'}</span>
                  )),
                ),
            },
            {
              title: '晋升职级',
              width: 120,
              render: (_: unknown, r: MergedRow) =>
                stack(
                  r.nominations.map((n) => (
                    <div key={n.id}>
                      {n.targetRank ? (
                        <Tag color="purple">{n.targetRank}</Tag>
                      ) : (
                        <span style={{ color: '#bbb' }}>-</span>
                      )}
                    </div>
                  )),
                ),
            },
            {
              title: '晋升理由',
              render: (_: unknown, r: MergedRow) =>
                stack(
                  r.nominations.map((n) => (
                    <div
                      key={n.id}
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.6,
                      }}
                    >
                      {r.nominations.length > 1 && (
                        <span
                          style={{
                            color: '#888',
                            fontSize: 12,
                            marginRight: 6,
                          }}
                        >
                          【{n.nominator?.name ?? '-'}】
                        </span>
                      )}
                      {n.highlight ?? '-'}
                    </div>
                  )),
                ),
            },
          ]}
        />
      </Card>
      <Modal
        title="人力资源部补录晋升提名"
        open={manualOpen}
        onCancel={() => {
          setManualOpen(false);
          form.resetFields();
        }}
        okText="提交"
        cancelText="取消"
        confirmLoading={manualMut.isPending}
        destroyOnClose
        onOk={async () => {
          try {
            const v = await form.validateFields();
            await manualMut.mutateAsync(v);
          } catch {
            /* 校验未过或请求失败由 message 提示 */
          }
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="nomineeId"
            label="被提名人"
            rules={[{ required: true, message: '请选择被提名人' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="请选择"
              options={candidates.map((c) => ({
                value: c.id,
                label: `${c.name ?? '(无姓名)'}${c.deptPath ? ` — ${c.deptPath}` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="targetRank" label="拟晋升职级">
            <Input placeholder="选填，如「主管」「高级专员」" />
          </Form.Item>
          <Form.Item
            name="highlight"
            label="晋升理由"
            rules={[{ required: true, message: '请填写晋升理由' }]}
          >
            <Input.TextArea rows={4} placeholder="必填" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
